import { useCallback, useEffect } from "react";
import { filterSearch } from "@/lib/columnFilters";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { TasksClient } from "@/features/tasks/TasksClient";
import {
  listTasks,
  listTaskCategories,
  tasksKeys,
  DEFAULT_TASK_SORT,
  DEFAULT_TASK_ORDER,
  type TasksSearch,
} from "@/features/tasks/tasks.api";
import { listMailboxes, mailKeys } from "@/features/mail/mail.api";
import { applyParamUpdates } from "@/lib/searchParams";
import { rememberListSearch } from "@/lib/listState";
import { hasModule, firstAllowedRoute } from "@/lib/auth";

// Module-gated (Beheer → Rechten); task visibility is additionally enforced
// server-side (personal tasks + shared mailboxes the user is a member of).
export const Route = createFileRoute("/_shell/tasks/")({
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "tasks")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  validateSearch: (search: Record<string, unknown>): TasksSearch => {
    const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
    return {
      ...filterSearch(search),
      view: str(search.view),
      mailboxId: str(search.mailboxId),
      status: str(search.status),
      priority: str(search.priority),
      categoryId: str(search.categoryId),
      q: str(search.q),
      cursor: str(search.cursor),
      limit: str(search.limit),
      sort: str(search.sort),
      order: str(search.order),
      period: str(search.period),
      // Typed or shared as ?withOrder=1 it arrives as a number; the toggle writes the string.
      withOrder: search.withOrder === 1 || search.withOrder === "1" ? "1" : undefined,
    };
  },
  component: TasksScreen,
});

function TasksScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();

  // Which slice you are looking at is part of where you are: the back link on a
  // task reads this, so returning from a task lands on the tab you left, not on
  // "Alles".
  useEffect(() => {
    rememberListSearch("/tasks", search);
  }, [search]);

  // The UI default hides done + cancelled; "all" sends no status filter.
  const effectiveStatus = search.status ?? "active";
  // Ordered by follow-up date unless the caller picked a column, because that is
  // what the date groups in the list are built on.
  const effectiveSort = search.sort ?? DEFAULT_TASK_SORT;
  const effectiveOrder = search.order ?? DEFAULT_TASK_ORDER;
  const tasksQuery = useQuery({
    queryKey: tasksKeys.list({
      ...search,
      status: effectiveStatus,
      sort: effectiveSort,
      order: effectiveOrder,
    }),
    queryFn: () =>
      listTasks({
        ...search,
        status: effectiveStatus === "all" ? undefined : effectiveStatus,
        sort: effectiveSort,
        order: effectiveOrder,
      }),
    placeholderData: keepPreviousData,
  });
  const mailboxesQuery = useQuery({
    queryKey: mailKeys.mailboxes,
    queryFn: () => listMailboxes(),
  });
  const categoriesQuery = useQuery({
    queryKey: tasksKeys.categoriesAll,
    queryFn: () => listTaskCategories(false, "all"),
  });

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      navigate({
        search: (prev) => applyParamUpdates(prev, updates),
        replace: true,
      });
    },
    [navigate],
  );

  return (
    <div className="wl-page wl-page--list" data-screen-label="Back-office Tasks">
      <TasksClient
        initialItems={tasksQuery.data?.data ?? []}
        pagination={
          tasksQuery.data?.pagination ?? {
            limit: 50,
            hasNextPage: false,
            nextCursor: null,
          }
        }
        search={search}
        updateParams={updateParams}
        mailboxes={mailboxesQuery.data ?? []}
        userId={session.userId}
        categories={categoriesQuery.data ?? []}
        isFetching={tasksQuery.isFetching}
        isLoading={tasksQuery.isLoading}
      />
    </div>
  );
}
